/* eslint-disable react/no-unescaped-entities */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  FileText, Copy, ExternalLink, Loader2, TrendingUp, Clock, Euro,
  CircleDollarSign, Hourglass, XCircle, CheckCircle2, RefreshCw, Eraser,
} from 'lucide-react';
import { toast } from 'sonner';

const CUR = { eur: '€', usd: '$', gbp: '£', pln: 'zł', sek: 'kr' };
const money = (v, cur = 'eur') => `${Number(v || 0).toFixed(2)} ${CUR[cur] || cur?.toUpperCase() || '€'}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};
const fmtDelay = (sec) => {
  if (sec == null) return '—';
  if (sec < 90) return `${Math.round(sec)} s`;
  if (sec < 5400) return `${Math.round(sec / 60)} min`;
  if (sec < 172800) return `${(sec / 3600).toFixed(1)} h`;
  return `${(sec / 86400).toFixed(1)} j`;
};

const STATUS_BADGE = {
  paid: { label: 'Payée', cls: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
  pending: { label: 'En attente', cls: 'bg-amber-50 border-amber-200 text-amber-700' },
  cancelled: { label: 'Annulée', cls: 'bg-zinc-100 border-zinc-200 text-zinc-500' },
};
const statusKind = (s) => (s === 'paid' ? 'paid' : ['cancelled', 'failed'].includes(s) ? 'cancelled' : 'pending');

const RANGES = [
  { value: 7, label: '7 jours' },
  { value: 30, label: '30 jours' },
  { value: 90, label: '90 jours' },
];

const DELAY_OPTIONS = [
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 60, label: '1 heure' },
  { value: 120, label: '2 heures' },
];

// Auto-cleanup control: voids stale open invoices + cancels stale incomplete
// PaymentIntents on Stripe. Backed by /invoices/cleanup-* endpoints.
const CleanupCard = ({ onCleaned }) => {
  const [st, setSt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);

  const loadSettings = useCallback(async () => {
    try {
      const { data } = await api.get('/invoices/cleanup-settings');
      setSt(data);
    } catch { /* silent */ }
  }, []);
  useEffect(() => { loadSettings(); }, [loadSettings]);

  const update = async (patch) => {
    if (!st) return;
    setSaving(true);
    try {
      const { data } = await api.put('/invoices/cleanup-settings', {
        enabled: patch.enabled ?? st.enabled,
        delay_minutes: patch.delay_minutes ?? st.delay_minutes,
      });
      setSt(data);
      toast.success('Réglage de nettoyage enregistré');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Enregistrement impossible');
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    const totals = { invoices_voided: 0, pis_cancelled: 0, invoices_finalized: 0, pis_finalized: 0, sessions_expired: 0, sessions_finalized: 0, errors: 0 };
    try {
      for (let i = 0; i < 12; i += 1) {
        const { data } = await api.post('/invoices/cleanup-now');
        ['invoices_voided', 'pis_cancelled', 'invoices_finalized', 'pis_finalized', 'sessions_expired', 'sessions_finalized', 'errors'].forEach((k) => { totals[k] += data[k] || 0; });
        if ((data.remaining_invoices || 0) + (data.remaining_pis || 0) === 0) break;
      }
      setLastRun(totals);
      const finalized = totals.invoices_finalized + totals.pis_finalized + totals.sessions_finalized;
      toast.success(
        `Nettoyage terminé — ${totals.invoices_voided} facture(s), ${totals.pis_cancelled} PI et ${totals.sessions_expired} lien(s) annulés`
        + (finalized ? `, ${finalized} paiement(s) retrouvé(s) payé(s) et finalisé(s)` : ''),
      );
      await loadSettings();
      onCleaned?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Nettoyage impossible');
    } finally {
      setRunning(false);
    }
  };

  const stale = st ? (st.stale_invoices || 0) + (st.stale_pis || 0) : 0;
  return (
    <Card className="card-flat p-5 mb-6" data-testid="invoices-cleanup-card">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <div className="min-w-[220px] max-w-xl">
          <h2 className="text-sm font-semibold flex items-center gap-2">
            <Eraser className="h-4 w-4 text-zinc-400" /> Nettoyage Stripe automatique
          </h2>
          <p className="text-xs text-zinc-500 mt-1">
            Annule les factures ouvertes et les PaymentIntents incomplets non payés après le délai. Le statut live est re-vérifié avant chaque annulation — jamais de paiement en cours ou payé touché.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-zinc-500">Actif</span>
          <Switch
            checked={!!st?.enabled}
            disabled={!st || saving}
            onCheckedChange={(v) => update({ enabled: v })}
            data-testid="invoices-cleanup-enabled"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-zinc-500">Délai</span>
          <Select
            value={String(st?.delay_minutes ?? 30)}
            onValueChange={(v) => update({ delay_minutes: parseInt(v, 10) })}
            disabled={!st || saving}
          >
            <SelectTrigger className="h-9 w-[130px] rounded-md text-xs" data-testid="invoices-cleanup-delay">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DELAY_OPTIONS.map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-3 ml-auto">
          {st && (
            <span className={`text-xs font-mono ${stale ? 'text-amber-600' : 'text-zinc-400'}`} data-testid="invoices-cleanup-stale">
              {stale ? `${st.stale_invoices} facture(s) + ${st.stale_pis} PI à nettoyer` : 'Rien à nettoyer ✓'}
            </span>
          )}
          <Button
            onClick={runNow}
            disabled={running || !st}
            className="btn-primary rounded-md h-9 text-xs"
            data-testid="invoices-cleanup-now"
          >
            {running ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Nettoyage…</> : <><Eraser className="h-3.5 w-3.5 mr-1.5" /> Nettoyer maintenant</>}
          </Button>
        </div>
      </div>
      {lastRun && (
        <div className="mt-3 pt-3 border-t border-zinc-100 text-[11px] text-zinc-500 font-mono" data-testid="invoices-cleanup-result">
          Dernier passage : {lastRun.invoices_voided} facture(s) · {lastRun.pis_cancelled} PI · {lastRun.sessions_expired} lien(s) annulés
          {(lastRun.invoices_finalized + lastRun.pis_finalized + lastRun.sessions_finalized) > 0 && ` · ${lastRun.invoices_finalized + lastRun.pis_finalized + lastRun.sessions_finalized} finalisé(s) payé(s)`}
          {lastRun.errors > 0 && ` · ${lastRun.errors} erreur(s)`}
        </div>
      )}
    </Card>
  );
};


const Kpi = ({ icon: Icon, label, value, sub, accent, testid }) => (
  <Card className="card-flat p-4 sm:p-5" data-testid={testid}>
    <div className="flex items-center gap-2 text-xs text-zinc-500 mb-2">
      <Icon className={`h-3.5 w-3.5 ${accent || 'text-zinc-400'}`} /> {label}
    </div>
    <div className="font-display text-xl sm:text-2xl font-bold tracking-tight tabular-nums">{value}</div>
    {sub && <div className="text-[11px] text-zinc-500 mt-1">{sub}</div>}
  </Card>
);

const SplitBars = ({ rows, total, testid }) => (
  <div className="space-y-2.5" data-testid={testid}>
    {rows.length === 0 && <div className="text-xs text-zinc-400">Aucune facture payée sur la période.</div>}
    {rows.map((r) => {
      const pct = total > 0 ? (r.revenue / total) * 100 : 0;
      return (
        <div key={r.store_id || r.account_id || r.name}>
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="font-medium text-zinc-800 truncate">{r.name || '—'}</span>
            <span className="font-mono text-zinc-600 shrink-0 ml-2">{money(r.revenue)} · {r.count}</span>
          </div>
          <div className="h-1.5 rounded-full bg-zinc-100 overflow-hidden">
            <div className="h-full rounded-full bg-zinc-950 transition-all" style={{ width: `${Math.max(2, pct)}%` }} />
          </div>
        </div>
      );
    })}
  </div>
);

export default function Invoices() {
  const [days, setDays] = useState(30);
  const [since, setSince] = useState('');
  const [until, setUntil] = useState('');
  const [mode, setMode] = useState('all'); // 'all' | 'invoice' | 'link'
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState('all');
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const LIMIT = 50;

  const rangeParams = useCallback(() => {
    const p = new URLSearchParams();
    if (since && until) {
      p.set('since', new Date(since).toISOString());
      p.set('until', new Date(new Date(until).getTime() + 86400000).toISOString());
    } else {
      p.set('days', String(days));
    }
    if (storeId !== 'all') p.set('store_id', storeId);
    if (mode !== 'all') p.set('mode', mode);
    return p;
  }, [days, since, until, storeId, mode]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const sp = rangeParams();
      const lp = rangeParams();
      if (status !== 'all') lp.set('status', status);
      if (search.trim()) lp.set('q', search.trim());
      lp.set('limit', String(LIMIT));
      lp.set('skip', String(skip));
      const [s, l] = await Promise.all([
        api.get(`/invoices/stats?${sp.toString()}`),
        api.get(`/invoices/list?${lp.toString()}`),
      ]);
      setStats(s.data);
      setRows(l.data.items);
      setTotal(l.data.total);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Chargement des factures impossible');
    } finally {
      setLoading(false);
    }
  }, [rangeParams, status, search, skip]);

  useEffect(() => { api.get('/stores').then((r) => setStores(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSkip(0); }, [days, since, until, storeId, status, search, mode]);

  const copyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Lien de paiement copié');
    } catch {
      window.prompt('Copie le lien de paiement :', url);
    }
  };

  const convDelta = useMemo(() => {
    if (!stats?.element?.created) return null;
    return (stats.conversion_rate - stats.element.conversion_rate).toFixed(1);
  }, [stats]);

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-7xl" data-testid="admin-invoices-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="overline mb-2">— Poste de commandement</div>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Factures & Liens</h1>
          <p className="text-zinc-600 mt-2">Suivi des ventes hébergées Stripe (factures « Pay by Stripe » et liens de paiement uniques) : conversion, encaissements et relances.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex p-1 bg-zinc-100 rounded-lg" data-testid="invoices-mode-picker">
            {[['all', 'Tous'], ['invoice', 'Factures'], ['link', 'Liens']].map(([k, lbl]) => (
              <button
                key={k}
                type="button"
                data-testid={`invoices-mode-${k}`}
                onClick={() => setMode(k)}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition ${
                  mode === k ? 'bg-zinc-950 text-white shadow-sm' : 'text-zinc-500 hover:text-zinc-800'
                }`}
              >
                {lbl}
              </button>
            ))}
          </div>
          <div className="flex p-1 bg-zinc-100 rounded-lg" data-testid="invoices-range-picker">
            {RANGES.map((r) => (
              <button
                key={r.value}
                type="button"
                data-testid={`invoices-range-${r.value}`}
                onClick={() => { setSince(''); setUntil(''); setDays(r.value); }}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition ${
                  !since && days === r.value ? 'bg-zinc-950 text-white shadow-sm' : 'text-zinc-500 hover:text-zinc-800'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <Input type="date" value={since} onChange={(e) => setSince(e.target.value)} className="h-9 w-[140px] rounded-md text-xs" data-testid="invoices-since-input" />
          <Input type="date" value={until} onChange={(e) => setUntil(e.target.value)} className="h-9 w-[140px] rounded-md text-xs" data-testid="invoices-until-input" />
          <Button variant="outline" size="icon" className="h-9 w-9 rounded-md" onClick={load} data-testid="invoices-refresh-button" title="Rafraîchir">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <Kpi icon={FileText} label="Factures créées" value={stats ? stats.created : '—'} testid="invoices-kpi-created" />
        <Kpi icon={CheckCircle2} label="Payées" value={stats ? stats.paid : '—'} accent="text-emerald-600" testid="invoices-kpi-paid" />
        <Kpi
          icon={Hourglass} label="En attente" value={stats ? stats.pending : '—'} accent="text-amber-600"
          sub={stats ? `${money(stats.pending_amount)} à encaisser` : ''} testid="invoices-kpi-pending"
        />
        <Kpi icon={XCircle} label="Annulées / expirées" value={stats ? stats.cancelled : '—'} testid="invoices-kpi-cancelled" />
        <Kpi
          icon={TrendingUp} label="Conversion (créées → payées)"
          value={stats ? `${stats.conversion_rate}%` : '—'} accent="text-emerald-600"
          sub={stats?.element?.created ? `Element : ${stats.element.conversion_rate}% (${convDelta > 0 ? '+' : ''}${convDelta} pts)` : 'Aucune vente Element sur la période'}
          testid="invoices-kpi-conversion"
        />
        <Kpi icon={Euro} label="CA encaissé (factures)" value={stats ? money(stats.revenue) : '—'} testid="invoices-kpi-revenue" />
        <Kpi icon={CircleDollarSign} label="Panier moyen" value={stats ? money(stats.avg_basket) : '—'} testid="invoices-kpi-basket" />
        <Kpi icon={Clock} label="Délai moyen de paiement" value={stats ? fmtDelay(stats.avg_delay_seconds) : '—'} sub="création → paiement" testid="invoices-kpi-delay" />
      </div>

      {/* Splits */}
      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        <Card className="card-flat p-5">
          <h2 className="text-sm font-semibold mb-4 flex items-center gap-2">CA factures par boutique</h2>
          <SplitBars rows={stats?.by_store || []} total={stats?.revenue || 0} testid="invoices-split-store" />
        </Card>
        <Card className="card-flat p-5">
          <h2 className="text-sm font-semibold mb-4 flex items-center gap-2">CA factures par compte Stripe</h2>
          <SplitBars rows={stats?.by_account || []} total={stats?.revenue || 0} testid="invoices-split-account" />
        </Card>
      </div>

      {/* Cleanup */}
      <CleanupCard onCleaned={load} />

      {/* Ledger */}
      <Card className="card-flat overflow-hidden">
        <div className="p-4 flex flex-wrap items-center gap-2 border-b border-zinc-100">
          <div className="flex p-1 bg-zinc-100 rounded-lg" data-testid="invoices-status-filter">
            {[['all', 'Toutes'], ['pending', 'En attente'], ['paid', 'Payées'], ['cancelled', 'Annulées']].map(([k, lbl]) => (
              <button
                key={k}
                type="button"
                data-testid={`invoices-filter-${k}`}
                onClick={() => setStatus(k)}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition ${
                  status === k ? 'bg-zinc-950 text-white shadow-sm' : 'text-zinc-500 hover:text-zinc-800'
                }`}
              >
                {lbl}
              </button>
            ))}
          </div>
          <Select value={storeId} onValueChange={setStoreId}>
            <SelectTrigger className="h-9 w-[190px] rounded-md text-xs" data-testid="invoices-store-filter">
              <SelectValue placeholder="Toutes les boutiques" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les boutiques</SelectItem>
              {stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher client, email, n° facture…"
            className="h-9 rounded-md text-xs flex-1 min-w-[200px]"
            data-testid="invoices-search-input"
          />
          <span className="text-xs text-zinc-400 font-mono ml-auto" data-testid="invoices-total-count">{total} facture(s)</span>
        </div>

        {loading ? (
          <div className="h-40 flex items-center justify-center text-zinc-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-zinc-500" data-testid="invoices-empty">
            <FileText className="h-8 w-8 mx-auto text-zinc-300 mb-2" />
            Aucune facture sur cette période / ces filtres.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-zinc-400 border-b border-zinc-100">
                  <th className="px-4 py-2.5 font-semibold">Facture</th>
                  <th className="px-4 py-2.5 font-semibold">Client</th>
                  <th className="px-4 py-2.5 font-semibold">Boutique</th>
                  <th className="px-4 py-2.5 font-semibold">Compte Stripe</th>
                  <th className="px-4 py-2.5 font-semibold text-right">Montant</th>
                  <th className="px-4 py-2.5 font-semibold">Statut</th>
                  <th className="px-4 py-2.5 font-semibold">Créée</th>
                  <th className="px-4 py-2.5 font-semibold">Payée</th>
                  <th className="px-4 py-2.5 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const kind = statusKind(r.payment_status);
                  const badge = STATUS_BADGE[kind];
                  return (
                    <tr key={r.id} className="border-b border-zinc-50 hover:bg-zinc-50/60" data-testid={`invoice-row-${r.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <span className={`px-1.5 py-0.5 rounded border font-mono text-[10px] ${r.mode === 'link' ? 'bg-sky-50 border-sky-200 text-sky-700' : 'bg-indigo-50 border-indigo-200 text-indigo-700'}`}>
                            {r.mode === 'link' ? 'lien' : 'facture'}
                          </span>
                          <span className="font-mono text-xs text-zinc-800">{r.invoice_id ? `…${r.invoice_id.slice(-8)}` : '—'}</span>
                        </div>
                        <div className="text-[11px] text-zinc-400 truncate max-w-[180px]" title={r.items_summary}>{r.items_summary}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-zinc-900 text-xs">{r.customer_name || '—'}</div>
                        <div className="text-[11px] text-zinc-500">{r.customer_email}</div>
                      </td>
                      <td className="px-4 py-3 text-xs text-zinc-700">{r.store_name || '—'}</td>
                      <td className="px-4 py-3 text-xs text-zinc-700">{r.account_name || '—'}</td>
                      <td className="px-4 py-3 text-right font-mono font-semibold tabular-nums">{money(r.amount, r.currency)}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-md border font-mono text-[11px] ${badge.cls}`} data-testid={`invoice-status-${r.id}`}>{badge.label}</span>
                        {r.order_name && <div className="text-[10px] text-zinc-400 mt-0.5 font-mono">{r.order_name}</div>}
                      </td>
                      <td className="px-4 py-3 text-xs text-zinc-500 font-mono whitespace-nowrap">{fmtDate(r.created_at)}</td>
                      <td className="px-4 py-3 text-xs text-zinc-500 font-mono whitespace-nowrap">{fmtDate(r.paid_at)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {r.invoice_url && kind === 'pending' && (
                            <Button
                              variant="outline" size="sm" className="h-7 rounded-md text-[11px] px-2"
                              onClick={() => copyLink(r.invoice_url)}
                              data-testid={`invoice-copy-link-${r.id}`}
                              title="Copier le lien de paiement (à renvoyer au client)"
                            >
                              <Copy className="h-3 w-3 mr-1" /> Lien
                            </Button>
                          )}
                          {r.invoice_url && (
                            <a
                              href={r.invoice_url} target="_blank" rel="noreferrer"
                              className="p-1.5 rounded-md hover:bg-zinc-100 text-zinc-500"
                              data-testid={`invoice-open-${r.id}`}
                              title="Ouvrir la facture hébergée Stripe"
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {total > LIMIT && (
          <div className="p-3 flex items-center justify-between border-t border-zinc-100 text-xs">
            <span className="text-zinc-500 font-mono">{skip + 1}–{Math.min(skip + LIMIT, total)} sur {total}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="h-7 rounded-md text-xs" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - LIMIT))} data-testid="invoices-prev-page">Précédent</Button>
              <Button variant="outline" size="sm" className="h-7 rounded-md text-xs" disabled={skip + LIMIT >= total} onClick={() => setSkip(skip + LIMIT)} data-testid="invoices-next-page">Suivant</Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
